import { Currency } from "@redotech/money/currencies";
import { WidgetFees } from "@redotech/redo-model/widget";
import {
  DeliveryType,
  ExtensionShopifyClient,
  ShopifyCart,
  ShopifyCartAttributes,
  ShopifyCartChange,
  ShopifyCartItem,
  ShopifyClientType,
  ShopifyProduct,
  ShopifyProductJS,
  ShopifyShop,
} from "@redotech/shopify-client/ajax";
import { log } from "../monitoring/log";
import { DELIVER_TO_STORE_CHECKED_SELECTOR } from "../selectors";

const SFCC_REDO_PRODUCT_ID = "x-redo"; // SFCC uses skus as ids which we mgr doesnt support yet. "x-redo" string, hopefully unique enough.

/**
 * Commerce Cloud (SFCC) StoreFront Client
 * Implements ExtensionShopifyClient interface for Salesforce Commerce Cloud integration
 */
export class CommercecloudStorefrontClient implements ExtensionShopifyClient {
  type = ShopifyClientType.COMMERCE_CLOUD;
  private readonly cartUrl: string;

  constructor({ storeUrl, locale }: { storeUrl: string; locale?: string }) {
    this.cartUrl = `/on/demandware.store/Sites-${CommercecloudStorefrontClient.getSiteId({ storeUrl })}-Site/${locale ?? CommercecloudStorefrontClient.getLocaleFromPathname()}`;
  }

  async cartAdd(
    data: { items: Partial<ShopifyCartItem>[] },
    params?: URLSearchParams,
    preventReload?: boolean,
  ): Promise<void> {
    if (data.items.length !== 1) {
      return;
    }

    const item = data.items[0];
    if (!item?.id && !item?.variant_id) {
      throw new Error("Item ID or variant ID is required");
    }

    let productId = String(item.id || item.variant_id);
    if (productId === SFCC_REDO_PRODUCT_ID) {
      productId = "x-redo";
    }
    const quantity = item.quantity || 1;

    await fetch(`${this.cartUrl}/Cart-AddProduct`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        pid: productId,
        quantity: quantity.toString(),
      }),
    });
  }

  async cartGet(signal?: AbortSignal): Promise<ShopifyCart> {
    const response = await fetch(`${this.cartUrl}/Cart-Get`, { signal });
    const data = await response.json();

    const items: ShopifyCartItem[] = (data?.items ?? []).map((item: any) => ({
      id: item.id || "",
      line_id: item.UUID, // UUID is a 26 character hex string, so do simple hash
      product_id: item.masterPid || "",
      variant_id: item.id || "",
      handle: item.id || "",
      title: item.productName || "",
      vendor: item.brand || "",
      quantity: item.quantity || 0,
      requires_shipping: true,
      line_price:
        Number(
          item.price.sales?.value ||
            item.price.list?.value || // In practice I have only seen item.price.list: null, but I expect it will be there as a full number sometimes
            item.price?.min?.sales?.value ||
            0,
        ) *
        (item.quantity || 0) *
        100,
      properties: null,
    }));

    if (items.some((item) => item.line_price === 0)) {
      log("WARN: Item has line price of 0");
    }

    return {
      attributes: {},
      items,
      currency: Currency.USD,
      total_price: data.totals.grandTotalValue,
      delivery_type: CommercecloudStorefrontClient.getCheckoutDeliveryType(),
    };
  }

  async cartChange(
    change: ShopifyCartChange,
    signal?: AbortSignal,
  ): Promise<void> {
    // Handle quantity update
    if (change.quantity !== undefined) {
      const params = new URLSearchParams();
      params.append("pid", String(change.id || ""));
      params.append("uuid", String(change.line || ""));
      params.append("quantity", String(change.quantity));

      await fetch(`${this.cartUrl}/Cart-UpdateQuantity?${params}`, { signal });
    } else {
      // Handle item removal (quantity 0 case)
      const params = new URLSearchParams();
      params.append("pid", String(change.id || ""));
      params.append("uuid", String(change.line || ""));

      await fetch(`${this.cartUrl}/Cart-RemoveProductLineItem?${params}`, {
        signal,
      });
    }

    location.reload();
  }

  async removeItemFromCart(
    item: ShopifyCartItem,
    numToRemove: number,
    signal?: AbortSignal,
  ): Promise<undefined> {
    const params = new URLSearchParams();
    params.append("pid", String(item.variant_id));
    params.append("uuid", String(item.line_id || item.id));

    if (numToRemove >= item.quantity) {
      // Remove entire item
      await fetch(`${this.cartUrl}/Cart-RemoveProductLineItem?${params}`, {
        signal,
      });
    } else {
      // Update quantity
      const newQuantity = item.quantity - numToRemove;
      params.append("quantity", String(newQuantity));
      await fetch(`${this.cartUrl}/Cart-UpdateQuantity?${params}`, { signal });
    }
  }

  async cartClear(): Promise<void> {
    return;
  }

  async updateCartAttribute(
    attributes: ShopifyCartAttributes,
    signal?: AbortSignal,
  ): Promise<ShopifyCart | void> {
    log(
      "updateCartAttribute() was called, but is unsupported for Commerce Cloud.",
    );
    return;
  }

  async updateCartItemSellingPlan(
    item: ShopifyCartItem,
    sellingPlanId: number | null,
    signal?: AbortSignal,
  ): Promise<undefined> {
    log(
      "updateCartItemSellingPlan() was called, but is unsupported for Commerce Cloud.",
    );
  }

  async getCollections(
    handle?: string,
    signal?: AbortSignal,
  ): Promise<{ title: string; handle: string }[] | undefined> {
    log(
      "updateCartItemSellingPlan() was called, but is unsupported for Commerce Cloud.",
    );

    return [];
  }

  async productGet(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProduct> {
    throw new Error("Unsupported");
  }

  async productGetJS(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProductJS> {
    throw new Error("Unsupported");
  }

  async getShopMetafield(
    data: { key: string; namespace: string },
    signal?: AbortSignal,
  ): Promise<ShopifyShop | undefined> {
    throw new Error("Unsupported");
  }

  async deleteCartAttribute(
    data: { key: string },
    signal?: AbortSignal,
  ): Promise<void> {
    log(
      "deleteCartAttribute() was called, but is unsupported for Commerce Cloud.",
    );
  }

  static getSiteId = ({ storeUrl }: { storeUrl: string }) => {
    if (
      location.pathname.startsWith("/us") ||
      location.pathname.startsWith("/global")
    ) {
      return "CityBeachUS";
    }

    if (location.pathname.startsWith("/uk")) {
      return "CityBeachNZ";
    }

    if (location.pathname.startsWith("/au")) {
      return "CityBeachAustralia";
    }

    return "CityBeachAustralia";
  };

  static getLocaleFromPathname = () => {
    if (location.pathname.startsWith("/us")) {
      return "en_US";
    }

    return "en_AU";
  };

  static getProductJsFromFees = ({
    fees,
  }: {
    fees: WidgetFees;
  }): ShopifyProductJS => {
    const mainProduct = {
      return: fees.returnProduct,
      package_protection: fees.packageProduct,
      "return,package_protection": fees.bothProduct,
    }[fees.mainType];

    if (!mainProduct) {
      throw new Error("Main product not found");
    }

    return {
      id: SFCC_REDO_PRODUCT_ID,
      price_min: Number(mainProduct!.variants![0]!.price) * 100,
      price_max: Number(mainProduct!.variants![0]!.price) * 100,
      variants: mainProduct!.variants!.map((variant) => ({
        id: variant.variant_id,
        price: Number(variant.price) * 100,
        requires_shipping: true,
      })),
    };
  };

  static isOnCheckoutPage(): boolean {
    return window.location.pathname.includes("/checkout");
  }

  static isOnPaymentSection(): boolean {
    return window.location.hash.includes("#payment");
  }

  static getCheckoutDeliveryType(): DeliveryType {
    if (!CommercecloudStorefrontClient.isOnCheckoutPage()) {
      return DeliveryType.UNKNOWN_OR_UNSELECTED;
    }

    if (document.querySelector(DELIVER_TO_STORE_CHECKED_SELECTOR)) {
      return DeliveryType.IN_STORE_PICKUP;
    }

    return DeliveryType.UNKNOWN_OR_UNSELECTED;
  }
}
